// ** Modules
import { getProviders } from 'next-auth/react';
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations';

// ** Components
import Loader from '@/components/common/Loader';
import DevErrorMode from '@/components/layout/DevErrorMode';
import MaintenanceMode from '@/components/layout/MaintenanceMode';
import StrapiPageRenderer from '@/components/strapi/StrapiPageRenderer';

// ** Views
import EventBookingPage from '@/views/Events/EventBookingPage';
import { EventDetailView } from '@/views/Events/EventDetailView';

// ** Apollo
import { getNewsBySlug } from '@/lib/utils/newsDataUtils';
import { initializeApollo } from '@/lib/apollo-client';
import { discoveryPagesQuery, pagesQuery } from '@/lib/utils/pageDataUtils';

// ** Utils
import { buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ** Types
import type { User } from 'next-auth';
import type { Page, News } from '@/types/graphql';
import type { AppContext } from '@/types/contextTypes';
import type { GetServerSideProps } from 'next';

export const VIEW_TYPES = {
	CMS: 'CMS_PAGE',
	NEWS: 'NEWS_DETAIL',
	EVENT: 'EVENT_DETAIL',
	EVENT_BOOKING: 'EVENT_BOOKING',
	EVENT_GUEST: 'EVENT_BOOKING_GUEST',
	PROFILE: 'USER_PROFILE'
} as const;

export type ViewType = (typeof VIEW_TYPES)[keyof typeof VIEW_TYPES];

type ViewProps =
	| { type: 'CMS_PAGE'; data: Page }
	| { type: 'NEWS_DETAIL'; data: News[]; slug: string }
	| { type: 'EVENT_DETAIL'; id: string; imageData?: any }
	| { type: 'EVENT_BOOKING' | 'EVENT_BOOKING_GUEST'; id: string }
	| { type: 'USER_PROFILE'; data: Page; slug: string };

const DynamicPageBySlug = (
	props: AppContext & {
		viewType: ViewProps['type'];
		devError?: any;
		slug?: string;
		params?: any;
		data?: any;
		newsBasePath?: string | null;
		eventsBasePath?: string | null;
		userBasePath?: string | null;
	}
) => {
	// const NO_HERO_SPACING = 40;
	const DEFAULT_SPACING = 12;

	const standaloneContainerSx = {
		py: DEFAULT_SPACING,
		pb: DEFAULT_SPACING
		// pt: NO_HERO_SPACING
	};

	const {
		viewType,
		data,
		host,
		locale,
		providers,
		currentUser,
		devError,
		slug,
		params,
		newsBasePath,
		eventsBasePath,
		userBasePath
	} = props;

	if (devError) return <DevErrorMode error={devError} slug={slug} />;
	if (!providers) return <MaintenanceMode />;

	const needsSsrData = ['CMS_PAGE', 'NEWS_DETAIL'].includes(viewType);
	if (needsSsrData && !data) return <Loader />;

	const renderProps = {
		pageContent: data,
		locale,
		providers: providers as any,
		currentUser: currentUser as User,
		host,
		newsBasePath,
		eventsBasePath,
		userBasePath
	};

	switch (viewType) {
		case 'EVENT_DETAIL':
			return (
				<EventDetailView
					id={params?.eventId}
					host={host}
					locale={locale}
					containerSx={standaloneContainerSx}
					imageData={data?.imageData || null}
					newsBasePath={newsBasePath}
					eventsBasePath={eventsBasePath}
					userBasePath={userBasePath}
				/>
			);

		case 'EVENT_BOOKING':
			return (
				<EventBookingPage
					eventId={params?.eventId}
					host={host}
					locale={locale}
					providers={providers}
					newsBasePath={newsBasePath}
					eventsBasePath={eventsBasePath}
					userBasePath={userBasePath}
					currentUser={currentUser}
					containerSx={standaloneContainerSx}
				/>
			);

		default:
			return <StrapiPageRenderer {...renderProps} />;
	}
};

export const getServerSideProps: GetServerSideProps = async (context) => {
	const { slug } = (context.params as { slug?: string | string[] }) ?? {};
	const slugArray = Array.isArray(slug) ? slug : slug ? [slug] : [];
	const rawPath = `/${slugArray.join('/')}`;
	const currentPath = rawPath.length > 1 && rawPath.endsWith('/') ? rawPath.slice(0, -1) : rawPath;

	if (
		currentPath.startsWith('/temp') ||
		currentPath.includes('/api/') ||
		currentPath.endsWith('.png') ||
		currentPath.endsWith('.jpg') ||
		currentPath.endsWith('.jpeg')
	) {
		return { notFound: true };
	}

	if (currentPath === '/404' || currentPath === '/500') {
		return { notFound: true };
	}

	const host = context.req.headers.host ?? 'localhost';
	const locale = context.locale as string;
	const isPreview = context.preview || false;
	const providers = (await getProviders()) ?? [];

	const client = initializeApollo({ host });
	const baseProps = { host, locale, providers };

	let sharedPaths = {
		newsBasePath: null as string | null,
		eventsBasePath: null as string | null,
		userBasePath: null as string | null
	};

	try {
		// 1. DISCOVERY
		const { data: discoveryData } = await client.query({
			query: discoveryPagesQuery,
			variables: {
				locale,
				filters: {
					or: [
						{ isNewsLandingPage: { eq: true } },
						{ isEventsLandingPage: { eq: true } },
						{ isUserLandingPage: { eq: true } }
					]
				}
			},
			fetchPolicy: 'no-cache'
		});

		const allPages = discoveryData?.pages || [];
		const newsLanding = allPages.find((page) => page?.isNewsLandingPage === true);
		const eventsLanding = allPages.find((page) => page?.isEventsLandingPage === true);
		const userLanding = allPages.find((page) => page?.isUserLandingPage === true);

		sharedPaths = {
			newsBasePath: newsLanding ? buildPathFromRelationLink(newsLanding).replace(/\/$/, '') : null,
			eventsBasePath: eventsLanding ? buildPathFromRelationLink(eventsLanding).replace(/\/$/, '') : null,
			userBasePath: userLanding ? buildPathFromRelationLink(userLanding).replace(/\/$/, '') : null
		};

		// --- RESTORED USER LANDING PAGE CHECK & REDIRECT ---
		if (sharedPaths.userBasePath && currentPath === sharedPaths.userBasePath) {
			try {
				const { data: childrenData } = await client.query({
					query: discoveryPagesQuery,
					variables: {
						locale,
						filters: {
							parent_page: { documentId: { eq: userLanding?.documentId } }
						}
					},
					fetchPolicy: 'no-cache'
				});

				const allChildren = childrenData?.pages || [];
				const targetChild =
					allChildren.find(
						(page) =>
							page?.slug.includes('mein-bereich') ||
							page?.slug.includes('dashboard') ||
							page?.slug.includes('uebersicht')
					) || allChildren[0];

				if (targetChild) {
					const redirectPath = buildPathFromRelationLink(targetChild);
					if (redirectPath !== currentPath) {
						return {
							redirect: {
								destination: redirectPath,
								permanent: false
							}
						};
					}
				}
			} catch (e: any) {
				console.error('✖ Redirect check failed:', e.message);
			}
		}

		// 🛡️ USER PROFILE DETAIL VIEW (If already inside sub-path)
		if (sharedPaths.userBasePath && currentPath.startsWith(sharedPaths.userBasePath)) {
			const lastSegment = slugArray[slugArray.length - 1] || '';
			const { data: pageResponse } = await client.query({
				query: pagesQuery,
				variables: {
					locale,
					slug: lastSegment,
					status: isPreview ? 'draft' : 'published'
				},
				fetchPolicy: 'no-cache'
			});

			const page = pageResponse?.pages?.[0];

			if (!page || currentPath !== buildPathFromRelationLink(page)) {
				return { notFound: true };
			}

			return {
				props: {
					...baseProps,
					viewType: 'USER_PROFILE',
					data: page,
					slug: currentPath,
					isPrivateRoute: page?.authenticated || false,
					metadata: { title: page?.title || 'User Profile' },
					...sharedPaths,
					...(await serverSideTranslations(locale, ['common', 'header', 'user']))
				}
			};
		}

		// 2. DYNAMIC NEWS DETAIL CHECK
		if (sharedPaths.newsBasePath) {
			const expectedPrefix = sharedPaths.newsBasePath;
			if (currentPath.startsWith(expectedPrefix)) {
				const baseSegments = sharedPaths.newsBasePath.split('/').filter(Boolean).length;
				const newsSlug = slugArray[baseSegments + 1];

				if (newsSlug) {
					const { data: newsData } = await client.query({
						query: getNewsBySlug,
						variables: {
							locale,
							slug: newsSlug,
							pagination: { page: 1, pageSize: 1 }
						},
						fetchPolicy: 'no-cache'
					});

					if (newsData?.allNews?.length) {
						const newsItem = newsData.allNews[0];
						return {
							props: {
								...baseProps,
								...(await serverSideTranslations(locale, ['common', 'header', 'news'])),
								viewType: 'NEWS_DETAIL',
								data: newsData.allNews,
								slug: newsSlug,
								isPrivateRoute: false,
								metadata: { title: newsItem?.newsTitle || 'News' },
								...sharedPaths
							}
						};
					}
				}
			}
		}

		// 3. EVENT DETAIL CHECK
		if (
			sharedPaths.eventsBasePath &&
			currentPath.startsWith(sharedPaths.eventsBasePath) &&
			currentPath !== sharedPaths.eventsBasePath
		) {
			const baseSegmentsCount = sharedPaths.eventsBasePath.split('/').filter(Boolean).length;

			const eventId = slugArray[baseSegmentsCount];
			const remainingSegments = slugArray.slice(baseSegmentsCount + 1);

			if (eventId) {
				let eventViewType: ViewType = VIEW_TYPES.EVENT;

				if (remainingSegments.length === 1) {
					const action = remainingSegments[0];
					if (action === 'book' || action === 'book-guest') {
						eventViewType = VIEW_TYPES.EVENT_BOOKING;
					} else {
						return { notFound: true };
					}
				} else if (remainingSegments.length > 1) {
					return { notFound: true };
				}

				return {
					props: {
						...baseProps,
						viewType: eventViewType,
						params: { eventId },
						slug: currentPath,
						isPrivateRoute: false,
						metadata: { title: 'Veranstaltung Details' },
						...sharedPaths,
						...(await serverSideTranslations(locale, ['common', 'form', 'header']))
					}
				};
			}
		}

		// 4. CMS PAGE FETCH
		const lastSegment = slugArray[slugArray.length - 1] || '';
		const { data: pageResponse } = await client.query({
			query: pagesQuery,
			variables: {
				locale,
				slug: lastSegment,
				status: isPreview ? 'draft' : 'published'
			},
			fetchPolicy: 'no-cache'
		});

		const page = pageResponse?.pages?.[0];

		if (!page || currentPath !== buildPathFromRelationLink(page)) {
			return { notFound: true };
		}

		return {
			props: {
				...baseProps,
				viewType: 'CMS_PAGE',
				data: page,
				isPrivateRoute: page?.authenticated || false,
				metadata: { title: page?.title || 'Page' },
				...sharedPaths,
				...(await serverSideTranslations(locale, ['common', 'header']))
			}
		};
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') {
			return {
				props: {
					...baseProps,
					viewType: 'CMS_PAGE',
					devError: {
						message: error.message,
						stack: error.stack,
						failedPath: currentPath
					},
					isPrivateRoute: false,
					...sharedPaths,
					...(await serverSideTranslations(locale, ['common', 'header']))
				}
			};
		}

		return { notFound: true };
	}
};

export default DynamicPageBySlug;
